import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Flex, Form, Modal, ModalProps, Skeleton, Typography } from 'antd'
import { ReasonTag } from 'src/app/shared/components/common/tag/ReasonTag'
import { CheckOutlined, PlusOutlined } from '@ant-design/icons'
import { Controller, useForm } from 'react-hook-form'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import ErrorText from 'src/app/shared/components/common/error-text'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import TextArea from 'antd/es/input/TextArea'
import { getDecisionReasonCodes } from 'src/app/shared/services/QuickExceptionService'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { REASON_TYPE } from '../constants/index.model'
import { makeAchExceptionDecision, makeCheckExceptionDecision } from 'src/app/shared/services/TransactionService'
import { DECISION_CODE, DECISION_TYPE, REASON_MAPPING } from '../constants'
import { GetClientListPayload } from 'src/app/shared/models/clientSetup.model'
import { IOuterTableRef } from 'src/app/shared/components/common/new-table/table.model'
import './index.scss'
import { isEmpty } from 'lodash'

const DecisionModal: React.FC<
  ModalProps & {
    icon?: ReactNode
    title: ReactNode
    titleDecision: ReactNode
    onClose: () => void
    description?: ReactNode
    isOpen: boolean
    footer?: any
    callbackAfterMakingDecision: () => void
    api: any
    decisionCode: string
    transactionType: number
    exceptionID: string
    handleSearch: (value: Partial<GetClientListPayload>) => Promise<any>
    isACH: boolean
    outerTableRef: React.RefObject<Partial<IOuterTableRef>>
    refreshSummaryAndBadges?: () => Promise<void>
  }
> = (props) => {
  const {
    isOpen,
    onClose,
    title,
    description,
    icon,
    titleDecision,
    api,
    decisionCode,
    transactionType,
    exceptionID,
    handleSearch,
    callbackAfterMakingDecision,
    isACH,
    outerTableRef,
    refreshSummaryAndBadges,
    ...rest
  } = props

  const [listReasons, setListReasons] = useState<REASON_TYPE[]>([])
  const [isShowReasonArea, setIsShowReasonArea] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingBtn, setIsLoadingBtn] = useState(false)
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    getValues,
    reset,
    resetField,
  } = useForm<any>({
    mode: 'all',
    defaultValues: {
      reasonCode: [],
      reasonText: '',
    },
  })

  useEffect(() => {
    if (isACH) {
      reset({
        reasonCode: ['UNAUTH'],
        reasonText: '',
      })
    }
  }, [isACH, reset])

  const { t } = useTranslation()
  const handleGetReasonDecision = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await getDecisionReasonCodes(decisionCode, transactionType)
      if (res?.statusCode === STATUS_CODES.SUCCESS) {
        let reasonResponse = res?.data
        if (decisionCode === DECISION_TYPE.PAY) {
          const otherOptionIdx = res?.data?.findIndex((reason: any) => reason?.label === REASON_MAPPING['OTHER'])
          const otherOption = reasonResponse[otherOptionIdx]

          reasonResponse.splice(otherOptionIdx, 1)
          reasonResponse.push(otherOption)
        }
        setListReasons(reasonResponse)
      }
    } catch (error: any) {
      api.error({
        message: `Get reasons decision failed`,
        description: `${error}`,
        placement: 'topRight',
      })
    } finally {
      setIsLoading(false)
    }
  }, [decisionCode, transactionType, api])

  useEffect(() => {
    if (isOpen) {
      handleGetReasonDecision()
    }
  }, [isOpen, handleGetReasonDecision])

  const onSubmit = async (values: any) => {
    setIsLoadingBtn(true)
    try {
      const payload = {
        id: exceptionID,
        decision: decisionCode === DECISION_TYPE.RETURN ? DECISION_CODE.RETURN : DECISION_CODE.PAY,
        reason: values?.reasonCode?.join(','),
        otherReason: values?.reasonText,
      }
      const res = isACH ? await makeAchExceptionDecision(payload) : await makeCheckExceptionDecision(payload)

      if (res?.statusCode === STATUS_CODES.SUCCESS) {
        api.success({
          message: t(
            'SystemMessages:' +
              (isACH
                ? SYSTEM_MESSAGE_IDS.DECISION_ACH_MADE_SUCCESSFULLY
                : SYSTEM_MESSAGE_IDS.DECISION_CHECK_MADE_SUCCESSFULLY),
          ),
        })
        await outerTableRef.current?.handleSearchLogic?.({})
        if (refreshSummaryAndBadges) {
          await refreshSummaryAndBadges()
        }
        handleCloseModal()
        if (callbackAfterMakingDecision) callbackAfterMakingDecision()
      }
    } catch (error: any) {
      api.error({
        message: `Make decision Failed`,
        description: `${error}`,
        placement: 'topRight',
      })
    } finally {
      setIsLoadingBtn(false)
    }
  }

  const handleCloseModal = () => {
    reset({
      reasonCode: isACH ? ['UNAUTH'] : [],
      reasonText: '',
    })
    setIsShowReasonArea(false)
    onClose()
  }

  return (
    <Modal
      className='decision-modal'
      centered
      onCancel={handleCloseModal}
      open={isOpen}
      keyboard={false}
      maskClosable={false}
      {...rest}
    >
      {icon ? <center>{icon}</center> : null}

      <Typography.Text className='title'>{title}</Typography.Text>

      <Typography.Text
        className='description'
        type='secondary'
        style={{
          textAlign: 'center',
          fontSize: 16,
        }}
      >
        {description}
      </Typography.Text>
      <Form onFinish={handleSubmit(onSubmit)}>
        <div className='reason-wrapper'>
          <Controller
            render={({ field: { onChange } }) => (
              <React.Fragment>
                <Typography.Text
                  strong
                  style={{ textAlign: 'left', fontSize: '14px' }}
                  className={DECISION_TYPE.RETURN === decisionCode ? 'required' : ''}
                >
                  {t('QuickExceptionProcessing:label.reason')}
                </Typography.Text>
                <Checkbox.Group
                  // defaultValue={[]}
                  onChange={(e) => {
                    onChange(e)
                    const otherOption = listReasons?.find((reason: REASON_TYPE) => reason?.label === 'Other')
                    const isShowReasonText = getValues('reasonCode')?.includes(otherOption?.value)
                    setIsShowReasonArea(isShowReasonText)
                    if (!isShowReasonText) {
                      resetField('reasonText')
                    }
                  }}
                  value={getValues('reasonCode')}
                >
                  <Flex className='reason-list'>
                    {isLoading ? (
                      <Skeleton />
                    ) : (
                      listReasons?.map((reason: REASON_TYPE, index: any) => {
                        return (
                          <Checkbox key={`reason-item-${reason?.value}`} value={reason?.value} name='reasonCode'>
                            <ReasonTag
                              icon={
                                getValues('reasonCode')?.includes(reason?.value) ? <CheckOutlined /> : <PlusOutlined />
                              }
                              value={reason?.label}
                              isSelect={getValues('reasonCode')?.includes(reason?.value)}
                            />
                          </Checkbox>
                        )
                      })
                    )}
                  </Flex>
                </Checkbox.Group>
              </React.Fragment>
            )}
            rules={{
              required: {
                value: DECISION_TYPE.RETURN === decisionCode,
                message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('QuickExceptionProcessing:label.reason'),
                }),
              },
            }}
            name='reasonCode'
            control={control}
          />
          <ErrorMessage errors={errors} name='reasonCode' render={({ message }) => <ErrorText>{message}</ErrorText>} />
          {isShowReasonArea && (
            <>
              <Controller
                render={({ field }) => (
                  <TextArea
                    {...field}
                    size='large'
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    className='reason-text-wrapper'
                    placeholder={t('QuickExceptionProcessing:placeholder.reason')}
                    onChange={(e: any) => {
                      field.onChange(e)
                    }}
                    maxLength={255}
                  />
                )}
                name='reasonText'
                control={control}
                rules={{
                  required: {
                    value: DECISION_TYPE.RETURN === decisionCode,
                    message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                      Required_Field: t('QuickExceptionProcessing:label.reasonOther'),
                    }),
                  },
                }}
              />
              <ErrorMessage
                errors={errors}
                name='reasonText'
                render={({ message }) => <ErrorText>{message}</ErrorText>}
              />
            </>
          )}
        </div>
        <Flex className='decision-modal-footer'>
          <Button size='middle' className='cancel-button' onClick={handleCloseModal}>
            {t('QuickExceptionProcessing:label.cancel')}
          </Button>
          <Button
            type='primary'
            loading={isLoadingBtn}
            htmlType='button'
            onClick={handleSubmit(onSubmit)}
            disabled={isLoadingBtn || !isEmpty(errors) || isLoadingBtn}
          >
            {titleDecision}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}

export default DecisionModal
